import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import {
  checkResData,
  formatDatetime,
  getConstructionsSelectBoxData
} from '../../../../utils/common';
import { TConstructionMinimal, TSelectItem } from '../../../../types/common';
import { getAllConstructionList } from '../../../../apicalls/construction';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';
import {
  TDailyReportListData,
  TDailyReportPageForm
} from '../../../../types/dailyReport';
import {
  exportDailyReportImage,
  getDailyReportList
} from '../../../../apicalls/dailyReport';
import {
  DAILY_REPORT_DVISION,
  DAILY_REPORT_DVISION_SUPERVISER,
  DAILY_REPORT_DVISION_OUTSOURCE
} from '../../../../const/DailyReportConst';
import { convertFormDateToApiDate, log } from '../../../../utils';
import { SITE_PROCESS_DISPLAY_LIMIT_LIST } from '../../../../const/ProcessConst';
import { parseNumber } from '../../../../utils/processAndTaskFormUtils';

export type Props = {
  defaultConstructId?: number;
  defaultStartDate?: string;
  defaultEndDate?: string;
  setValue: UseFormSetValue<TDailyReportPageForm>;
  reset: UseFormReset<TDailyReportPageForm>;
};
export const useHooks = (props: Props) => {
  const {
    defaultConstructId,
    defaultStartDate,
    defaultEndDate,
    setValue,
    reset
  } = props;

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // apiにリクエストするための検索用フォームの値
  // 検索ボタンが押された時の情報を保持しておく
  // フォームの値だけかえて検索ボタンを押されない時があるので、
  // apiリクエスト時はフォームの値を直接参照して使わず、この値を使う
  const [searchParams, setSearchParams] = useState<TDailyReportPageForm>({
    construct_id: { value: defaultConstructId || 0, label: '--' },
    start_date: defaultStartDate || '',
    end_date: defaultEndDate || '',
    report_division: '種別',
    cost_up: false,
    cost_down: false,
    images: false,
    worker_name: '',
    display_limit: String(SITE_PROCESS_DISPLAY_LIMIT_LIST[0])
  });

  // ペジネーションの現在のページ
  const [currentPage, setCurrentPage] = useState(1);

  // ペジネーションの総ページ数
  const [pageCount, setPageCount] = useState(1);

  // 再レンダリングのトリガ用state
  const [renderDailyReportTrigger, setRenderDailyReportTrigger] =
    useState(false);
  // 再レンダリング
  const reRenderDailyReport = () => {
    setRenderDailyReportTrigger(prev => !prev);
  };

  // ロード中、検索ボタンによる検索実行中の判定
  const [isLoading, setIsLoading] = useState(false);

  // ページネーションよるページ移動中の判定
  const [isMovingPage, setIsMovingPage] = useState(false);

  // 写真出力によるダウンロード中の判定
  const [isDownloading, setIsDownloading] = useState(false);

  // 検索実行中かどうか
  const isSearching = useRef(false);

  // 日報一覧データ
  const [dailyReportList, setDailyReportList] = useState<
    Array<TDailyReportListData>
  >([]);

  // 工事選択リスト
  const [constructList, setConstructList] = useState<TSelectItem[]>([
    { value: 0, label: '案件' }
  ]);

  // 選択した工事ID（モーダルで利用）
  const [constructId, setConstructId] = useState(0);
  // 選択した工事名
  const [constructName, setConstructName] = useState('');

  // ローディングアイコン制御用の工事ID
  const currentConstructId = useRef<number>();

  // TODO: iOS safariでチェックボックスによる絞り込みがされないためstateで保持
  // 原価UPチェックボックスの値
  const [checkCostUp, setCheckCostUp] = useState(false);
  // 原価Downチェックボックスの値
  const [checkCostDown, setCheckCostDown] = useState(false);
  // 写真ありチェックボックスの値
  const [checkImages, setCheckImages] = useState(false);
  // 原価UPチェックボックス変更時の処理
  const handleOnChangeCostUp = () => {
    setCheckCostUp(prev => !prev);
    setValue('cost_up', !checkCostUp);
  };
  // 原価Downチェックボックス変更時の処理
  const handleOnChangeCostDown = () => {
    setCheckCostDown(prev => !prev);
    setValue('cost_down', !checkCostDown);
  };
  // 写真ありチェックボックス変更時の処理
  const handleOnChangeImages = () => {
    setCheckImages(prev => !prev);
    setValue('images', !checkImages);
  };

  // 追加モーダル
  const [isOpenDailyReportAddModal, setIsOpenDailyReportAddModal] =
    useState(false);

  // URL発行モーダル
  const [isOpenDailyReportUrlModal, setIsOpenDailyReportUrlModal] =
    useState(false);

  // 詳細モーダル
  const [detailDailyReportId, setDetailDailyReportId] = useState(0);
  const [isOpenDailyReportDetailModal, setIsOpenDailyReportDetailModal] =
    useState(false);

  // 編集モーダル用
  const [editDailyReportId, setEditDailyReportId] = useState(0);
  const [isOpenDailyReportEditModal, setIsOpenDailyReportEditModal] =
    useState(false);

  const [deleteDailyReportId, setDeleteDailyReportId] = useState(0);
  const [isOpenDailyReportDeleteModal, setIsOpenDailyReportDeleteModal] =
    useState(false);

  // 期間終了日付
  const [endDate, setEndDate] = useState('');

  // 期間終了日付変更時のハンドラ
  const handleChangeEndDatePicker = (e: any) => {
    setEndDate(e.target.value);
  };

  // 工事選択時の処理。各モーダルで利用する
  const handleOnChangeConstructId = (selectedValue: TSelectItem) => {
    if (selectedValue && selectedValue.value) {
      setConstructId(selectedValue.value);
      setConstructName(selectedValue.label);
    } else {
      setConstructId(0);
      setConstructName('');
    }
  };

  // 追加ボタン押下時の処理
  const handleOnClickAddButton = () => {
    setIsOpenDailyReportAddModal(true);
  };

  // URL発行ボタン押下時の処理
  const handleOnClickUrlButton = () => {
    setIsOpenDailyReportUrlModal(true);
  };

  // 詳細ボタン押下時の処理
  const handleOnClickDetailButton = (dailyReportId: number) => {
    setDetailDailyReportId(dailyReportId);
    setIsOpenDailyReportDetailModal(true);
  };
  // 編集ボタン押下時の処理
  const handleOnClickEditButton = (
    dailyReportId: number,
    reportDivision: number
  ) => {
    setEditDailyReportId(dailyReportId);
    if (reportDivision === DAILY_REPORT_DVISION_SUPERVISER) {
      setIsOpenDailyReportEditModal(true);
    } else {
      // 職人作成分の日報編集画面を開く
    }
  };

  // 削除ボタン押下時処理
  const handleOnClickDeleteButton = (dailyReportId: number) => {
    setDeleteDailyReportId(dailyReportId);
    setIsOpenDailyReportDeleteModal(true);
  };

  // 写真出力ボタン押下時の処理
  const handleOnClickExportImagesButton = (dailyReportId: number) => {
    // ローディング開始
    setIsDownloading(true);
    downloadDailyReportImage(dailyReportId);
  };

  // 写真ダウンロード
  const downloadDailyReportImage = (dailyReportId: number) => {
    exportDailyReportImage(dailyReportId).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const currentDatetime = formatDatetime(new Date(), 'YYYYMMDD');
          const fileName = `daily_report_image_${currentDatetime}`;

          downloadImage(data, fileName);

          // ログ出力
          log.info(`export Images: report id: ${dailyReportId}`);
        }
        // ローディング終了
        setIsDownloading(false);
      });
    });
  };

  const downloadImage = (data: any, fileName: string) => {
    // ファイルへのリンク(aタグ)を作成してclickイベントでダウンロードさせる
    const url = URL.createObjectURL(
      new Blob([data], {
        type: data.type
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName + getExtension(data.type));
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    link.parentNode?.removeChild(link);
  };

  const getExtension = (contentType: string) => {
    if (contentType === 'application/zip') return '.zip';
    if (contentType === 'image/x-ms-bmp') return '.bmp';
    if (contentType === 'image/x-bmp') return '.bmp';
    if (contentType === 'image/bmp') return '.bmp';
    return `.${contentType.replace('image/', '')}`;
  };

  // 検索ボタン押下時の処理。searchParamsを更新して実際の検索はuseEffectにて行う。
  const handleOnClickSearch = (form: TDailyReportPageForm) => {
    setCurrentPage(1);
    setSearchParams({ ...form });
  };

  // 記入者種別名を取得
  const getReportDivisionName = (reportDivision?: Number) => {
    if (typeof reportDivision !== 'number') return '';
    return DAILY_REPORT_DVISION.filter(
      division => division.code === reportDivision
    )[0].name;
  };

  // value値から工事リストの該当要素を取得
  const getSelectItemByValue = (
    selectItemList: Array<TSelectItem>,
    value: number
  ) => {
    return (
      selectItemList.filter(construct => construct.value === value)[0] ?? {
        value: 0,
        label: '--'
      }
    );
  };

  const isEditableReport = (report: TDailyReportListData) => {
    // 職人が作成した日報は編集不可
    if (report.report_division === DAILY_REPORT_DVISION_OUTSOURCE) return false;
    return report.is_mine;
  };

  const isDeletableReport = (report: TDailyReportListData) => {
    // 職人が作成した日報は編集可能
    if (report.report_division === DAILY_REPORT_DVISION_OUTSOURCE) return true;
    return report.is_mine;
  };

  // 初回描画時の検索フォーム初期化
  useEffect(() => {
    // フォーム設定
    reset({ ...searchParams });

    // 工事セレクトボックスを取得
    getConstructionsSelectBoxData(setConstructList, enqueueSnackbar, {
      value: 0,
      label: '案件'
    });

    // 案件一覧取得と案件セレクトボックスの値設定
    getAllConstructionList().then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const resConstructList: TConstructionMinimal[] = data.data;
          if (resConstructList && resConstructList.length > 0) {
            const list: TSelectItem[] = [];
            list.push({ value: 0, label: '案件' });
            resConstructList.forEach((construct: TConstructionMinimal) => {
              list.push({
                value: construct.id,
                label: construct.name ?? ''
              });
            });
            setValue(
              'construct_id',
              getSelectItemByValue(list, defaultConstructId ?? 0)
            );
            // 工事IDの初期値がある場合は値を設定する
            if (defaultConstructId) {
              const selectValue = getSelectItemByValue(
                list,
                defaultConstructId
              );
              setConstructId(selectValue.value);
              setConstructName(selectValue.label);
            }
          }
        }
      });
    });
  }, []);

  // 初回表示、ページング時、日報の追加・編集・削除があったときの日報一覧取得
  useEffect(() => {
    if (isSearching.current) {
      return;
    }

    // 工事IDに変更がない場合はローディングアイコンの種類を変える
    if (currentConstructId.current !== searchParams.construct_id?.value) {
      setIsLoading(true);
      currentConstructId.current = searchParams.construct_id?.value;
    } else {
      setIsMovingPage(true);
    }

    isSearching.current = true;

    // 一覧取得
    const constructId = searchParams.construct_id
      ? searchParams.construct_id.value
      : 0;
    const reportDivision = parseNumber(searchParams.report_division);
    const displayLimit = parseNumber(searchParams.display_limit) || undefined;
    getDailyReportList(
      constructId,
      searchParams.worker_name,
      reportDivision,
      convertFormDateToApiDate(searchParams.start_date),
      convertFormDateToApiDate(searchParams.end_date),
      searchParams.cost_up,
      searchParams.cost_down,
      searchParams.images,
      currentPage,
      displayLimit
    ).then(resData => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          setDailyReportList(data.data);
          setPageCount(data.page_count);
        }
        setIsLoading(false);
        setIsMovingPage(false);
        isSearching.current = false;
      });
    });
  }, [searchParams, currentPage, renderDailyReportTrigger]);

  return {
    pageCount,
    currentPage,
    dailyReportList,
    setCurrentPage,
    constructId,
    constructName,
    constructList,
    handleOnChangeConstructId,
    handleOnClickSearch,
    getReportDivisionName,
    handleOnClickAddButton,
    isOpenDailyReportAddModal,
    setIsOpenDailyReportAddModal,
    handleOnClickUrlButton,
    isOpenDailyReportUrlModal,
    setIsOpenDailyReportUrlModal,
    handleOnClickDetailButton,
    detailDailyReportId,
    isOpenDailyReportDetailModal,
    setIsOpenDailyReportDetailModal,
    setDetailDailyReportId,
    handleOnClickEditButton,
    editDailyReportId,
    isOpenDailyReportEditModal,
    setIsOpenDailyReportEditModal,
    handleOnClickExportImagesButton,
    endDate,
    handleChangeEndDatePicker,
    isEditableReport,
    searchParams,
    reRenderDailyReport,
    handleOnClickDeleteButton,
    isDeletableReport,
    isOpenDailyReportDeleteModal,
    setIsOpenDailyReportDeleteModal,
    deleteDailyReportId,
    isMovingPage,
    isLoading,
    isDownloading,
    renderDailyReportTrigger,
    checkCostUp,
    handleOnChangeCostUp,
    checkCostDown,
    handleOnChangeCostDown,
    checkImages,
    handleOnChangeImages
  };
};
